import { HttpClient } from '@angular/common/http';
import {
	ApplicationRef,
	ChangeDetectorRef,
	Component,
	ComponentRef,
	createComponent,
	EnvironmentInjector,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	ViewContainerRef,
} from '@angular/core';
import { CORES, legendaNDCInfo } from '@componentes/mapa-paraiba-svg/legenda';
import Leaflet, { LayerGroup } from 'leaflet';
import { RelatorioNDCResponse } from '../../../interfaces/relatorio-ndc';
import { PopupNdcComponent } from '../../popup-ndc/popup-ndc.component';
import { ColorUtils } from '@utils/color-utils';
import {
	AgrupamentoGeojson,
	agrupamentoGeojsonMap,
} from '../../../enum/Agrupamento';
import {
	AgrupamentoResponse,
	FormularioRelatorio,
	INPUTS_RELATORIOS,
} from '../../../interfaces';
import { FormBuilder } from '@angular/forms';
import { CamadasGeojson, camadasGeojsonMap } from '../../../enum/Camadas';
import { MenuMapaPluviometriaObservadaComponent } from '../../menu-mapa-pluviometria-observada/menu-mapa-pluviometria-observada.component';
import { Feature, Geometry } from 'geojson';
import { isNuloOuUndefined } from '@utils';
import html2canvas from 'html2canvas';

type MapCapture = {
	dataUrl: string;
	mapRect: { x: number; y: number; w: number; h: number };
	canvasW: number;
	canvasH: number;
};

@Component({
	selector: 'seira-mapa-ndc-chuva',
	templateUrl: './mapa-ndc.component.html',
	styleUrls: ['./mapa-ndc.component.scss'],
})
export class MapaNdcComponent implements OnDestroy, OnChanges {
	markersLayer: LayerGroup | any;
	@Input() dados?: RelatorioNDCResponse[];
	map!: Leaflet.Map;
	geoJsonLayer?: Leaflet.GeoJSON;
	centerDefault: Leaflet.LatLngExpression = [-7.2605416, -36.7290255];
	zoomDefault = 8;
	layersCamada = new EventEmitter<Leaflet.GeoJSON[]>();
	legenda = legendaNDCInfo;
	menuLateral = new Leaflet.Control({ position: 'bottomright' });

	private inputs = inject(INPUTS_RELATORIOS);
	@Input() isLoading = false;
	dadosCarregados = false;
	MenuMapa?: ComponentRef<MenuMapaPluviometriaObservadaComponent>;
	agrupamentoResult: AgrupamentoResponse[] = [];
	private isExporting = false;
	private readonly exportScale = 2;
	private readonly exportDesktopWidth = 993;
	private readonly exportMapHeight = 500;
	private coordOverlayEl?: HTMLDivElement;
	private coordOverlayCleanup?: () => void;
	private compassOverlayEl?: HTMLDivElement;
	private compassOverlayCleanup?: () => void;
	maskLayer?: Leaflet.Polygon;
	private maskPaneName = 'maskPane';
	constructor(
		private fb: FormBuilder,
		private httpClient: HttpClient,
		private cdr: ChangeDetectorRef,
		private appRef: ApplicationRef,
		private envInjector: EnvironmentInjector,
		private viewContainerRef: ViewContainerRef
	) {}
	ngOnDestroy(): void {
		if (this.geoJsonLayer) {
			this.geoJsonLayer.remove();
			this.geoJsonLayer.clearLayers();
		}
		this.removerMascaraDoMapa();
		this.removerOverlays();
	}

	ngOnChanges(): void {
		this.cdr.detectChanges();
		if (!this.isLoading) {
			this.limparMapa();
			if (this.isBacia) {
				this.getMenu();
			} else {
				if (this.MenuMapa) {
					this.MenuMapa.destroy();
					this.MenuMapa = undefined;
				}
				setTimeout(() => {
					this.loadMapLayers(this.map!, false);
				}, 500);
			}
		}
	}

	setupMap(mapa: Leaflet.Map) {
		this.map = mapa;
		this.map.attributionControl?.remove();
		this.cdr.detectChanges();
	}
	get agrupamento(): string {
		return this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value;
	}
	get isBacia(): boolean {
		return (
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value ===
			'BACIA_SUBBACIA'
		);
	}
	get agrupamentoGeoJSON(): AgrupamentoGeojson {
		return (
			agrupamentoGeojsonMap[this.agrupamento] ?? AgrupamentoGeojson.MUNICIPIO
		);
	}
	getCamadaGeoJson(camada: string): CamadasGeojson {
		return camadasGeojsonMap[camada] ?? CamadasGeojson.BACIAS;
	}
	private limparMapa(): void {
		if (this.geoJsonLayer) {
			this.geoJsonLayer.remove();
			this.geoJsonLayer.clearLayers();
		}
	}
	getPropertyNomeGeoson(feature: {
		properties: {
			Nome: string;
			NM_MESO: string;
			name: string;
			nome: string;
			Regiao: string;
		};
	}) {
		return (
			feature.properties.Regiao ||
			feature.properties.Nome ||
			feature.properties.nome ||
			feature.properties.name ||
			feature.properties.NM_MESO
		);
	}
	resetMapView() {
		const defaultZoom = 8;
		this.map.setView(this.centerDefault, defaultZoom);
	}
	loadMapLayers(map: Leaflet.Map, deveLimparMapa = true, camada?: string) {
		if (deveLimparMapa) {
			this.limparMapa();
		}
		const renderer = Leaflet.canvas({ padding: 0.5 });
		if (this.isBacia) {
			const geojson = this.getCamadaGeoJson(camada!);
			this.httpClient.get<GeoJSON.GeoJsonObject>(geojson).subscribe({
				next: value => {
					this.geoJsonLayer = Leaflet.geoJSON(value, {
						renderer,
						smoothFactor: 1,
						style: (feature: any) =>
							this.handleGetStyleByFeature(feature, camada),
						onEachFeature: (feature: any, layer: any) => {
							layer.on({
								click: (e: any) => {
									const nome =
										feature.properties[
											this.chavePropriedadeFeatureGeoJSON(camada, feature)
										] ?? this.getPropertyNomeGeoson(feature);
									const ndc = this.handleFindNDCByPropertyName(nome);
									const markerPopup = this.handleAttachPopup(
										ndc
											? this.handleCreatePopupByNDC(
													ndc,

													nome
											  )
											: this.handleCreatePopup(nome)
									);
									layer.bindPopup(markerPopup).openPopup(e.latlng);
								},
							});
						},
					} as any).addTo(map);
				},
			});
		} else {
			this.httpClient
				.get<GeoJSON.GeoJsonObject>(this.agrupamentoGeoJSON)
				.subscribe({
					next: value => {
						this.geoJsonLayer = Leaflet.geoJSON(value, {
							renderer,
							smoothFactor: 1,
							style: (feature: any) => this.handleGetStyleByFeature(feature),
							onEachFeature: (feature: any, layer: any) => {
								layer.on({
									click: (e: any) => {
										const nome =
											feature.properties[
												this.chavePropriedadeFeatureGeoJSON(camada, feature)
											] ?? this.getPropertyNomeGeoson(feature);
										const ndc = this.handleFindNDCByPropertyName(nome);
										const markerPopup = this.handleAttachPopup(
											ndc
												? this.handleCreatePopupByNDC(
														ndc,

														nome
												  )
												: this.handleCreatePopup(nome)
										);
										layer.bindPopup(markerPopup).openPopup(e.latlng);
									},
								});
							},
						} as any).addTo(map);
					},
				});
		}
	}

	private handleCreatePopup(titulo: string): ComponentRef<PopupNdcComponent> {
		const componentRef = createComponent(PopupNdcComponent, {
			environmentInjector: this.envInjector,
		});
		const color = CORES.CINZA;
		componentRef.instance.nome = titulo;
		componentRef.instance.headerColor = color;
		componentRef.instance.agrupamento = this.agrupamento;
		componentRef.instance.titleColor = ColorUtils.ehCorClara(color)
			? 'gray'
			: CORES.BRANCO_NEVE;
		return componentRef;
	}

	private handleCreatePopupByNDC(
		ndc: RelatorioNDCResponse,
		titulo: string
	): ComponentRef<PopupNdcComponent> {
		const componentRef = createComponent(PopupNdcComponent, {
			environmentInjector: this.envInjector,
		});
		componentRef.instance.ndc = ndc;
		componentRef.instance.nome = titulo;
		componentRef.instance.agrupamento = this.agrupamento;
		const color = this.getColorByDiasChuva(ndc.diasComChuva);
		componentRef.instance.headerColor = color;

		componentRef.instance.titleColor = ColorUtils.ehCorClara(color)
			? 'gray'
			: CORES.BRANCO_NEVE;
		return componentRef;
	}

	private handleAttachPopup(compRef: ComponentRef<PopupNdcComponent>) {
		this.appRef.attachView(compRef.hostView);
		compRef.onDestroy(() => this.appRef.detachView(compRef.hostView));

		const div = document.createElement('div');
		div.appendChild(compRef.location.nativeElement);
		return div;
	}

	private handleGetStyleByFeature(feature?: any, camada?: string) {
		const nome =
			feature.properties[
				this.chavePropriedadeFeatureGeoJSON(camada, feature)
			] ?? this.getPropertyNomeGeoson(feature);
		const ndc = this.handleFindNDCByPropertyName(nome);
		const corBase = ndc
			? this.getColorByDiasChuva(ndc.diasComChuva)
			: CORES.CINZA;
		const corViva = this.corMaisViva(corBase);
		const strokeColor = this.isExporting ? '#000000' : '#546E7A';
		const strokeOpacity = this.isExporting ? 0.6 : 0.65;
		const strokeWeight = this.isExporting ? 1.1 : 1.2;
		const dashArray = this.isExporting ? '1,1' : undefined;

		return {
			fillColor: corViva,
			weight: strokeWeight,
			opacity: strokeOpacity,
			color: strokeColor,
			dashArray,
			lineJoin: 'round' as const,
			lineCap: 'round' as const,
			fillOpacity: 0.95,
		};
	}
	private chavePropriedadeFeatureGeoJSON(
		camada?: string,
		feature?: Feature<Geometry, any>
	): string {
		switch (this.agrupamento) {
			case 'MUNICIPIO_POSTO':
			case 'MUNICIPIO':
			case 'REGIAO_PLUVIOMETRICA':
				return 'name';
			case 'MICRORREGIAO':
				return 'nome';
			case 'MESORREGIAO':
				return 'NM_MESO';
			case 'BACIA_SUBBACIA':
				if (camada === 'REGIAO_CURSO') {
					return 'Regiao';
				} else if (
					camada === 'BACIA_SUBBACIA_REGIAO' ||
					camada === 'BACIA_SUBBACIA'
				) {
					const bacia = this.agrupamentoResult.find(
						(d: AgrupamentoResponse) => d.nome === feature!.properties['name']
					);
					const subbacia = this.agrupamentoResult.find(
						(d: AgrupamentoResponse) => d.nome === feature!.properties['Nome']
					);
					const regiao = this.agrupamentoResult.find(
						(d: AgrupamentoResponse) => d.nome === feature!.properties['Regiao']
					);
					if (bacia! > subbacia! && bacia! > regiao!) {
						return 'name';
					} else if (subbacia! > bacia! && subbacia! > regiao!) {
						return 'Nome';
					} else if (regiao! > bacia! && regiao! > subbacia!) {
						return 'Regiao';
					}
				}
				return 'Nome';

			default:
				return 'Nome';
		}
	}

	private getchavePropriedadeFeature(): keyof RelatorioNDCResponse {
		if (this.agrupamento === 'MUNICIPIO_POSTO') {
			return 'municipio';
		}
		return 'nome';
	}

	private handleFindNDCByPropertyName(name: string) {
		return this.dados?.find(
			dado => dado[this.getchavePropriedadeFeature()] === name
		);
	}

	private getColorByDiasChuva(valor?: number): string {
		if (isNuloOuUndefined(valor)) {
			return CORES.CINZA;
		}
		const arr = Object.keys(legendaNDCInfo)
			.map(Number)
			.sort((a, b) => b - a);

		for (const key of arr) {
			if (valor >= key) {
				return legendaNDCInfo[key];
			}
		}
		return CORES.CINZA;
	}

	private hexToHsl(hex: string) {
		const c = hex.replace('#', '').trim();
		const r = parseInt(c.substring(0, 2), 16) / 255;
		const g = parseInt(c.substring(2, 4), 16) / 255;
		const b = parseInt(c.substring(4, 6), 16) / 255;

		const max = Math.max(r, g, b);
		const min = Math.min(r, g, b);
		let h = 0;
		let s = 0;
		const l = (max + min) / 2;

		if (max !== min) {
			const d = max - min;
			s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

			switch (max) {
				case r:
					h = (g - b) / d + (g < b ? 6 : 0);
					break;
				case g:
					h = (b - r) / d + 2;
					break;
				default:
					h = (r - g) / d + 4;
			}

			h /= 6;
		}

		return { h, s, l };
	}

	private hslToHex(h: number, s: number, l: number) {
		const hue2rgb = (p: number, q: number, t: number) => {
			if (t < 0) t += 1;
			if (t > 1) t -= 1;
			if (t < 1 / 6) return p + (q - p) * 6 * t;
			if (t < 1 / 2) return q;
			if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
			return p;
		};

		let r: number;
		let g: number;
		let b: number;

		if (s === 0) {
			r = g = b = l;
		} else {
			const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
			const p = 2 * l - q;
			r = hue2rgb(p, q, h + 1 / 3);
			g = hue2rgb(p, q, h);
			b = hue2rgb(p, q, h - 1 / 3);
		}

		const toHex = (v: number) =>
			Math.round(v * 255)
				.toString(16)
				.padStart(2, '0');

		return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
	}

	private corMaisViva(hex: string) {
		if (!hex || !hex.startsWith('#') || hex.length !== 7) return hex;

		const { h, s, l } = this.hexToHsl(hex);
		const s2 = Math.min(1, s * 1.25 + 0.08);
		const l2 = Math.max(0, Math.min(1, l * 0.98));

		return this.hslToHex(h, s2, l2);
	}

	async getMapImage(): Promise<MapCapture | null> {
		await new Promise(resolve => this.map.whenReady(() => resolve(true)));

		const root = document.getElementById('mapa-legenda');
		if (!root) return null;

		this.isExporting = true;
		root.classList.add('export-mode');
		let restoreMobileLayout: (() => void) | null = null;

		const geoJsonAtual = this.geoJsonLayer?.toGeoJSON();
		if (geoJsonAtual) {
			this.aplicarMascaraForaDoMapa(
				geoJsonAtual as GeoJSON.GeoJsonObject,
				this.map
			);
		}

		this.aplicarCoordenadas(this.map);
		this.renderizarCoordenadasNoMapa(this.map, this.geoJsonLayer?.getBounds());

		this.aplicarRosaDosVentos(this.map);
		this.renderizarRosaDosVentos();

		try {
			restoreMobileLayout = await this.ajustarLayoutMobileParaExportacao(root);
			const geoJsonAtualAjustado = this.geoJsonLayer?.toGeoJSON();
			if (geoJsonAtualAjustado) {
				this.aplicarMascaraForaDoMapa(
					geoJsonAtualAjustado as GeoJSON.GeoJsonObject,
					this.map
				);
			}
			await this.esperarRenderizacaoMapa();

			const canvas = await html2canvas(root, {
				useCORS: true,
				allowTaint: true,
				logging: false,
				scale: this.exportScale,
				windowWidth: window.innerWidth < 1200
					? this.exportDesktopWidth
					: window.innerWidth,
			});
			const leafletEl = root.querySelector(
				'.leaflet-container'
			) as HTMLElement | null;
			if (!leafletEl) return null;

			const rootRect = root.getBoundingClientRect();
			const mapRectDom = leafletEl.getBoundingClientRect();
			const relLeft = mapRectDom.left - rootRect.left;
			const relTop = mapRectDom.top - rootRect.top;

			return {
				dataUrl: canvas.toDataURL('image/png', 1),
				canvasW: canvas.width,
				canvasH: canvas.height,
				mapRect: {
					x: relLeft * this.exportScale,
					y: relTop * this.exportScale,
					w: mapRectDom.width * this.exportScale,
					h: mapRectDom.height * this.exportScale,
				},
			};
		} finally {
			if (restoreMobileLayout) restoreMobileLayout();
			this.removerMascaraDoMapa();
			this.removerOverlays();
			this.isExporting = false;
			root.classList.remove('export-mode');
		}
	}

	private async ajustarLayoutMobileParaExportacao(
		root: HTMLElement
	): Promise<(() => void) | null> {
		if (window.innerWidth >= 1200) return null;

		const mapaWrapper = root.querySelector(
			'.mapa-wrapper'
		) as HTMLElement | null;
		const mapaLeaflet = root.querySelector(
			'.leaflet-container'
		) as HTMLElement | null;

		const rootPrev = {
			width: root.style.width,
			minWidth: root.style.minWidth,
			maxWidth: root.style.maxWidth,
		};

		const wrapperPrev = mapaWrapper
			? {
					height: mapaWrapper.style.height,
					minHeight: mapaWrapper.style.minHeight,
			  }
			: null;

		const leafletPrev = mapaLeaflet
			? {
					height: mapaLeaflet.style.height,
			  }
			: null;

		root.style.width = `${this.exportDesktopWidth}px`;
		root.style.minWidth = `${this.exportDesktopWidth}px`;
		root.style.maxWidth = `${this.exportDesktopWidth}px`;

		if (mapaWrapper) {
			mapaWrapper.style.height = `${this.exportMapHeight}px`;
			mapaWrapper.style.minHeight = `${this.exportMapHeight}px`;
		}

		if (mapaLeaflet) {
			mapaLeaflet.style.height = `${this.exportMapHeight}px`;
		}

		this.map.invalidateSize(true);
		await this.esperarRenderizacaoMapa();

		return () => {
			root.style.width = rootPrev.width;
			root.style.minWidth = rootPrev.minWidth;
			root.style.maxWidth = rootPrev.maxWidth;

			if (mapaWrapper && wrapperPrev) {
				mapaWrapper.style.height = wrapperPrev.height;
				mapaWrapper.style.minHeight = wrapperPrev.minHeight;
			}

			if (mapaLeaflet && leafletPrev) {
				mapaLeaflet.style.height = leafletPrev.height;
			}

			this.map.invalidateSize(true);
		};
	}

	private async esperarRenderizacaoMapa() {
		await new Promise<void>(r => requestAnimationFrame(() => r()));
		await new Promise<void>(r => requestAnimationFrame(() => r()));
		await new Promise<void>(r => setTimeout(() => r(), 100));
	}

	private aplicarMascaraForaDoMapa(
		geo: GeoJSON.GeoJsonObject,
		map: Leaflet.Map
	) {
		if (this.maskLayer) {
			this.maskLayer.remove();
			this.maskLayer = undefined;
		}

		if (!map.getPane(this.maskPaneName)) {
			map.createPane(this.maskPaneName);
			const pane = map.getPane(this.maskPaneName)!;
			pane.style.zIndex = '350';
			pane.style.pointerEvents = 'none';
		}

		const holes: Leaflet.LatLngExpression[][] = [];

		const addPolygonOuterRingAsHole = (coords: any) => {
			if (!Array.isArray(coords) || !Array.isArray(coords[0])) return;

			const ring = coords[0] as [number, number][];
			const latlngs: Leaflet.LatLngExpression[] = ring.map(([lng, lat]) => [
				lat,
				lng,
			]);
			holes.push(latlngs);
		};

		const g: any = geo;

		if (g?.type === 'FeatureCollection' && Array.isArray(g.features)) {
			for (const f of g.features) {
				const geom = f?.geometry;
				if (!geom) continue;

				if (geom.type === 'Polygon') {
					addPolygonOuterRingAsHole(geom.coordinates);
				} else if (geom.type === 'MultiPolygon') {
					for (const polyCoords of geom.coordinates) {
						addPolygonOuterRingAsHole(polyCoords);
					}
				}
			}
		}

		const outer: Leaflet.LatLngExpression[] = [
			[90, -180],
			[90, 180],
			[-90, 180],
			[-90, -180],
		];

		this.maskLayer = Leaflet.polygon([outer, ...holes], {
			pane: this.maskPaneName,
			stroke: false,
			fillColor: '#FFFFFF',
			fillOpacity: 1,
			interactive: false,
		}).addTo(map);
	}

	private readonly coordenadasRef: {
		eixoVertical: Array<{ lat: number; label: string }>;
		eixoHorizontal: Array<{ lng: number; label: string }>;
	} = {
		eixoVertical: [
			{ lat: -5.9, label: '6.0° S' },
			{ lat: -6.5, label: '6,5° S' },
			{ lat: -7.0, label: '7,0° S' },
			{ lat: -7.5, label: '7,5° S' },
			{ lat: -8.0, label: '8,0° S' },
			{ lat: -8.4, label: '' },
		],
		eixoHorizontal: [
			{ lng: -38.5, label: '38,5° W' },
			{ lng: -38.0, label: '38,0° W' },
			{ lng: -37.5, label: '37.5° W' },
			{ lng: -37.0, label: '37,0° W' },
			{ lng: -36.5, label: '36,5° W' },
			{ lng: -36.0, label: '36,0° W' },
			{ lng: -35.5, label: '35,5° W' },
			{ lng: -35.0, label: '35,0° W' },
		],
	};

	private aplicarCoordenadas(map: Leaflet.Map) {
		if (this.coordOverlayEl) this.coordOverlayEl.remove();
		if (this.coordOverlayCleanup) this.coordOverlayCleanup();

		const container = map.getContainer() as HTMLElement;
		if (getComputedStyle(container).position === 'static') {
			container.style.position = 'relative';
		}

		const overlay = document.createElement('div');
		overlay.className = 'mapa-coordenadas-overlay';
		overlay.style.position = 'absolute';
		overlay.style.inset = '0';
		overlay.style.pointerEvents = 'none';
		overlay.style.overflow = 'hidden';
		overlay.style.zIndex = '9999';

		container.appendChild(overlay);
		this.coordOverlayEl = overlay;

		const render = () => {
			const bounds = this.geoJsonLayer?.getBounds();
			this.renderizarCoordenadasNoMapa(map, bounds);
		};

		map.whenReady(() => render());
		map.on('resize', render);
		map.on('moveend', render);
		map.on('zoomend', render);

		this.coordOverlayCleanup = () => {
			map.off('resize', render);
			map.off('moveend', render);
			map.off('zoomend', render);
		};
	}

	private renderizarCoordenadasNoMapa(
		map: Leaflet.Map,
		bounds?: Leaflet.LatLngBounds
	) {
		if (!this.coordOverlayEl) return;

		const size = map.getSize();
		if (!size || size.x < 50 || size.y < 50) return;

		const currentBounds = bounds ?? map.getBounds();
		const center = currentBounds.getCenter();
		const gutterLeft = 74;
		const padTop = 14;
		const padRight = 84;
		const tickLen = 10;
		const fontSize = 12;
		const gap = 3;

		const axisX = gutterLeft;
		const axisTop = padTop;
		const axisRight = size.x - padRight;
		const latBaseEixoX =
			this.coordenadasRef.eixoVertical[
				this.coordenadasRef.eixoVertical.length - 1
			]?.lat ?? -8.39;
		const yBaseRaw = map.latLngToContainerPoint([latBaseEixoX, center.lng]).y;
		const lineColor = this.isExporting ? 'rgba(0,0,0,1)' : 'rgba(0,0,0,.28)';
		const textColor = this.isExporting ? 'rgba(0,0,0,1)' : 'rgba(0,0,0,.55)';
		const maxAxisY = size.y - (tickLen + 18);
		const minAxisY = axisTop + 40;
		const axisY = Math.round(Math.min(maxAxisY, Math.max(minAxisY, yBaseRaw)));

		const eixoLinhas = `
			<div style="position:absolute;left:${axisX}px;top:${axisTop}px;width:1px;height:${Math.max(
				0,
				axisY - axisTop
			)}px;background:${lineColor};"></div>
			<div style="position:absolute;left:${axisX}px;top:${axisY}px;height:1px;width:${Math.max(
				0,
				axisRight - axisX
			)}px;background:${lineColor};"></div>
			<div style="position:absolute;left:${axisX}px;top:${axisTop}px;height:1px;width:${Math.max(
				0,
				axisRight - axisX
			)}px;background:${lineColor};"></div>
			<div style="position:absolute;left:${axisRight}px;top:${axisTop}px;width:1px;height:${Math.max(
				0,
				axisY - axisTop
			)}px;background:${lineColor};"></div>
		`;

		const ticksY = this.coordenadasRef.eixoVertical
			.map(t => {
				const yRaw = map.latLngToContainerPoint([t.lat, center.lng]).y;
				if (yRaw < axisTop - 80 || yRaw > axisY + 80) return '';

				const y = Math.round(Math.min(axisY, Math.max(axisTop, yRaw)));
				const xLabel = axisX - tickLen - gap;

				return `
					<div style="position:absolute;left:${xLabel}px;top:${y}px;transform:translate(-100%,-50%);color:${textColor};font-size:${fontSize}px;line-height:1;white-space:nowrap;">
						<span style="display:inline-block;transform:rotate(-90deg);transform-origin:100% 50%;">${t.label}</span>
					</div>
					<div style="position:absolute;left:${axisX}px;top:${y}px;width:${tickLen}px;height:1px;background:${lineColor};transform:translateY(-50%);"></div>
				`;
			})
			.join('');

		const ticksX = this.coordenadasRef.eixoHorizontal
			.map(t => {
				const xRaw = map.latLngToContainerPoint([center.lat, t.lng]).x;
				if (xRaw < axisX - 80 || xRaw > axisRight + 80) return '';

				const x = Math.round(Math.min(axisRight, Math.max(axisX, xRaw)));

				return `
					<div style="position:absolute;left:${x}px;top:${
						axisY - tickLen
					}px;width:1px;height:${tickLen}px;background:${lineColor};transform:translateX(-50%);"></div>
					<div style="position:absolute;left:${x}px;top:${
						axisY + tickLen - 5
					}px;transform:translateX(-50%);color:${textColor};font-size:${fontSize}px;line-height:1;white-space:nowrap;">${
						t.label
					}</div>
				`;
			})
			.join('');

		const midY = Math.round((axisTop + axisY) / 2);
		const midX = Math.round((axisX + axisRight) / 2);
		const titulosEixos = this.isExporting
			? `
				<div style="position:absolute;left:${
					axisX - 30
				}px;top:${midY}px;transform:translate(-100%,-50%) rotate(-90deg);transform-origin:100% 50%;color:${textColor};font-size:${
					fontSize + 2
				}px;white-space:nowrap;">Latitude</div>
				<div style="position:absolute;left:${midX}px;top:${
					axisY + tickLen + 12
				}px;transform:translateX(-50%);color:${textColor};font-size:${
					fontSize + 2
				}px;white-space:nowrap;">Longitude</div>
			`
			: '';

		this.coordOverlayEl.innerHTML = `${eixoLinhas}${ticksY}${ticksX}${titulosEixos}`;
	}

	private aplicarRosaDosVentos(map: Leaflet.Map) {
		if (this.compassOverlayEl) this.compassOverlayEl.remove();
		if (this.compassOverlayCleanup) this.compassOverlayCleanup();

		const container = map.getContainer() as HTMLElement;
		if (getComputedStyle(container).position === 'static') {
			container.style.position = 'relative';
		}

		const overlay = document.createElement('div');
		overlay.className = 'mapa-rosa-ventos-overlay';
		overlay.style.position = 'absolute';
		overlay.style.right = '95px';
		overlay.style.top = '30px';
		overlay.style.left = 'auto';
		overlay.style.width = '60px';
		overlay.style.height = '60px';
		overlay.style.pointerEvents = 'none';
		overlay.style.zIndex = '9999';

		container.appendChild(overlay);
		this.compassOverlayEl = overlay;

		const render = () => this.renderizarRosaDosVentos();
		map.whenReady(() => render());
		map.on('resize', render);

		this.compassOverlayCleanup = () => {
			map.off('resize', render);
		};
	}

	private renderizarRosaDosVentos() {
		if (!this.compassOverlayEl) return;

		const preto = '#111827';
		const cinza = '#9CA3AF';

		this.compassOverlayEl.innerHTML = `
			<svg viewBox="0 0 100 100" width="60" height="60" style="display:block;">
				<circle cx="50" cy="52" r="32" fill="none" stroke="${cinza}" stroke-width="2"/>
				<polygon points="50,18 57,52 50,86 43,52" fill="${cinza}"/>
				<polygon points="18,52 50,59 82,52 50,45" fill="${cinza}"/>
				<polygon points="50,26 54,52 50,78 46,52" fill="${cinza}" opacity="0.9" transform="rotate(45 50 52)"/>
				<circle cx="50" cy="52" r="4" fill="${cinza}"/>
				<text x="50" y="10" text-anchor="middle" font-size="11" font-weight="700" fill="${preto}">N</text>
				<text x="76" y="22" text-anchor="middle" font-size="10" font-weight="700" fill="${preto}">NE</text>
				<text x="24" y="22" text-anchor="middle" font-size="10" font-weight="700" fill="${preto}">NO</text>
				<text x="92" y="55" text-anchor="middle" font-size="11" font-weight="700" fill="${preto}">L</text>
				<text x="8" y="55" text-anchor="middle" font-size="11" font-weight="700" fill="${preto}">O</text>
				<text x="76" y="92" text-anchor="middle" font-size="10" font-weight="700" fill="${preto}">SE</text>
				<text x="24" y="92" text-anchor="middle" font-size="10" font-weight="700" fill="${preto}">SO</text>
				<text x="50" y="99" text-anchor="middle" font-size="11" font-weight="700" fill="${preto}">S</text>
			</svg>
		`;
	}

	private isMobileViewport(): boolean {
		return window.matchMedia('(max-width: 768px)').matches;
	}

	private removerOverlays() {
		if (this.coordOverlayEl) this.coordOverlayEl.remove();
		if (this.coordOverlayCleanup) this.coordOverlayCleanup();
		this.coordOverlayEl = undefined;
		this.coordOverlayCleanup = undefined;

		if (this.compassOverlayEl) this.compassOverlayEl.remove();
		if (this.compassOverlayCleanup) this.compassOverlayCleanup();
		this.compassOverlayEl = undefined;
		this.compassOverlayCleanup = undefined;
	}

	private removerMascaraDoMapa() {
		if (this.maskLayer) {
			this.maskLayer.remove();
			this.maskLayer = undefined;
		}
	}

	getMenu() {
		if (this.dados?.length === 0) {
			return;
		}
		if (this.MenuMapa && !this.isBacia) {
			this.MenuMapa.destroy();
			this.MenuMapa = undefined;
		} else {
			this.menuLateral.onAdd = (map: Leaflet.Map) => {
				this.MenuMapa = this.viewContainerRef.createComponent(
					MenuMapaPluviometriaObservadaComponent
				);
				this.MenuMapa.instance.map = map;
				this.MenuMapa.changeDetectorRef.detectChanges();
				this.MenuMapa.instance.gerarRelatorio.subscribe(value => {
					this.isLoading = true;
					this.MenuMapa!.instance.isLoadingMap = true;
					this.MenuMapa!.changeDetectorRef.detectChanges();
					this.loadMapLayers(map, true, value);
					setTimeout(() => {
						this.isLoading = false;
						this.MenuMapa!.instance.isLoadingMap = false;
						this.MenuMapa!.changeDetectorRef.detectChanges();
					}, 2500);
				});
				return this.MenuMapa.location.nativeElement;
			};
			this.menuLateral.addTo(this.map);
		}
	}
}
